import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useLazyGetResidentDetailsQuery } from '@/store/api';
import { handleApiError, EnhancedError } from '@/utils/admin/errorHandling';
import { IResidentDetailsParams } from '@/types/admin/residentDetails';

export const useResidentDetails = ({ accountId, leaseId, mode }: IResidentDetailsParams) => {
  const [
    getResidentDetails,
    {
      data,
      isLoading,
      isError,
      error,
      isUninitialized,
      isFetching,
    }
  ] = useLazyGetResidentDetailsQuery();

  // Error handling state
  const [enhancedError, setEnhancedError] = useState<EnhancedError | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  
  // Track if component is mounted to prevent memory leaks
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch resident details
  const fetchResidentDetails = useCallback(async () => {
    if (!accountId || !leaseId) {
      console.warn('Missing accountId or leaseId, cannot fetch resident details');
      return;
    }

    // Clear previous error
    setEnhancedError(null);

    try {
      abortControllerRef.current = new AbortController();
      await getResidentDetails({ accountId, leaseId, mode }).unwrap();
      // Reset retry count on success
      if (isMounted.current) {
        setRetryCount(0);
      }
    } catch (fetchError) {
      // Only update state if component is still mounted
      if (isMounted.current) {
        const errorDetails = handleApiError(fetchError, '/api/v1/residents/details', retryCount);
        setEnhancedError(errorDetails);
        setRetryCount(prev => prev + 1);
        
        console.error('🔴 Failed to fetch resident details after retries:', errorDetails);
      }
    }
  }, [accountId, leaseId, getResidentDetails, mode]);

  // Trigger fetch when component mounts or params change
  useEffect(() => {
    isMounted.current = true;
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    fetchResidentDetails();

    // Cleanup function to prevent memory leaks and cancel pending retries
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, [accountId, leaseId, mode, fetchResidentDetails]);

  // Process resident details data
  const residentDetails = useMemo(() => {
    if (!data) return null;
    
    // Map the data to match the expected format
    return {
      // Basic info
      accountId: data.accountId,
      leaseId: leaseId, // Use the parameter, not data.leaseId
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      customerType: data.customerType,
      
      // Lease info
      leaseStatus: data.leaseStatus,
      propertyName: data.propertyName,
      buildingUnitNumberSpace: data.buildingUnitNumberSpace,
      
      // Account info
      hasResioAccount: !!data.accountId,
      lastInteractionDate: data.lastInteractionDate,
      lastPaymentDate: data.lastPaymentDate,
      hasRecurringPayments: data.hasRecurringPayments,
      
      // Related accounts
      linkedAccounts: data.linkedAccounts || [],
      roommates: data.roommates || [],
    };
  }, [data, leaseId]);

  // Manual retry function
  const refetch = useCallback(() => {
    if (isMounted.current) {
      setEnhancedError(null);
      fetchResidentDetails();
    }
  }, [fetchResidentDetails]);

  return {
    residentDetails,
    isLoading: isLoading || isUninitialized || isFetching,
    isError,
    error,
    enhancedError,
    refetch,
    retryCount,
    canRetry: enhancedError?.shouldRetry || false,
  };
};
